import React, { useState, useEffect } from "react";
import { Provider as JotaiProvider, useAtom, useSetAtom } from "jotai";
import { Alert, Button, DatePicker, Form, InputNumber, Modal, Select, Space, Upload, message } from "antd";
import { InboxOutlined, ShoppingCartOutlined, UploadOutlined, WarningOutlined } from "@ant-design/icons";
import moment from "moment";
import { Container } from "../../../components/Container";
import { FiltroProgramarVacaciones } from "../programar-vacaciones/componentes/FiltroProgramarVacaciones";
import { AprobarButton } from "../programar-vacaciones/ProgramarVacacionesPage";
import { anioAtom, recargarTablaAtom } from "../programar-vacaciones/ProgramarVacacionesStore";
import TablaCompraVacaciones from "./componentes/TablaCompraVacaciones";
import { CompraVacacionesService } from "services/VacacionesService";

const anioActual = new Date().getFullYear();
const OPCIONES_ANIO = [{ value: anioActual, label: String(anioActual) }];

/* ── Modal: Importar ────────────────────────────────────────────── */
const ImportarModal = () => {
    const [form] = Form.useForm();
    const [visibleModal, setVisibleModal] = useState(false);
    const [archivo, setArchivo] = useState(null);
    const [loading, setLoading] = useState(false);
    const [hayErrores, setHayErrores] = useState(false);
    const [anioFiltro] = useAtom(anioAtom);
    const setRecargar = useSetAtom(recargarTablaAtom);

    const onAbrir = () => {
        setVisibleModal(true);
        setArchivo(null);
        setHayErrores(false);
        form.setFieldsValue({ anio: anioFiltro });
    };

    const onCerrar = () => {
        setVisibleModal(false);
        setHayErrores(false);
    };

    const onConfirmar = async () => {
        const valores = await form.validateFields();
        if (!archivo) {
            message.warning("Seleccione un archivo Excel antes de importar.");
            return;
        }
        setLoading(true);
        setHayErrores(false);
        const formData = new FormData();
        formData.append("anio", String(valores.anio));
        formData.append("excel", archivo);
        const response = await CompraVacacionesService.importar(formData);
        setLoading(false);

        if (response.esArchivo) {
            const url = URL.createObjectURL(response.blob);
            const a = document.createElement("a");
            a.href = url;
            a.download = "errores_compra_vacaciones.xlsx";
            a.click();
            URL.revokeObjectURL(url);
            setHayErrores(true);
        } else if (response.estado === "EXITO") {
            setVisibleModal(false);
            message.success(`Se importaron ${response.data} registros correctamente.`);
            setRecargar((v) => v + 1);
        } else {
            message.error(response.mensaje || "No se pudo procesar la importación.");
        }
    };

    return (
        <>
            <Button type="primary" icon={<UploadOutlined />} onClick={onAbrir}>
                Importar Excel
            </Button>
            <Modal
                title="Importar Compra de Vacaciones"
                open={visibleModal}
                onCancel={onCerrar}
                footer={[
                    <Button key="cancelar" onClick={onCerrar}>Cancelar</Button>,
                    <Button key="importar" type="primary" loading={loading} onClick={onConfirmar}>Importar</Button>,
                ]}
            >
                <Form form={form} layout="vertical">
                    <Form.Item
                        label="Año"
                        name="anio"
                        rules={[{ required: true, message: "Seleccione el año." }]}
                    >
                        <Select style={{ width: 120 }} options={OPCIONES_ANIO} />
                    </Form.Item>
                </Form>
                <Upload.Dragger
                    name="file"
                    multiple={false}
                    accept=".xlsx,.xls"
                    fileList={archivo ? [archivo] : []}
                    beforeUpload={(file) => { setArchivo(file); return false; }}
                    onRemove={() => setArchivo(null)}
                    maxCount={1}
                >
                    <p className="ant-upload-drag-icon"><InboxOutlined /></p>
                    <p className="ant-upload-text">Haz clic o arrastra el archivo aquí</p>
                    <p className="ant-upload-hint">
                        Columnas requeridas: <strong>DNI, DIAS</strong>
                        <br />
                        Columna opcional: <strong>FECHA EMISION</strong> (si se llena, reemplaza la fecha de emisión del documento generado)
                    </p>
                </Upload.Dragger>

                {hayErrores && (
                    <Alert
                        style={{ marginTop: 16 }}
                        icon={<WarningOutlined />}
                        message="Se encontraron errores — ningún registro fue insertado"
                        description="Se descargó automáticamente un Excel con el detalle de cada error. Corrija los datos y vuelva a importar."
                        type="warning"
                        showIcon
                    />
                )}
            </Modal>
        </>
    );
};

/* ── Modal: Editar ──────────────────────────────────────────────── */
const EditarModal = ({ registro, onCerrar }) => {
    const [form] = Form.useForm();
    const [loading, setLoading] = useState(false);
    const setRecargar = useSetAtom(recargarTablaAtom);

    useEffect(() => {
        if (registro) {
            form.setFieldsValue({
                diasComprados: registro.diasComprados,
                fechaEmision: registro.fechaEmision ? moment(registro.fechaEmision, "DD/MM/YYYY") : null,
            });
        }
    }, [registro]);

    const onConfirmar = async () => {
        const valores = await form.validateFields();
        setLoading(true);
        const response = await CompraVacacionesService.editar(registro.id, {
            diasComprados: valores.diasComprados,
            fechaEmision: valores.fechaEmision ? valores.fechaEmision.startOf("day").format("YYYY-MM-DDTHH:mm:ss") : null,
        });
        setLoading(false);
        if (response.estado === "EXITO") {
            message.success("Compra actualizada.");
            setRecargar((v) => v + 1);
            onCerrar();
        } else {
            message.error(response.mensaje || "No se pudo actualizar.");
        }
    };

    return (
        <Modal
            title="Editar Compra de Vacaciones"
            open={!!registro}
            onCancel={onCerrar}
            footer={[
                <Button key="cancelar" onClick={onCerrar}>Cancelar</Button>,
                <Button key="guardar" type="primary" loading={loading} onClick={onConfirmar}>Guardar</Button>,
            ]}
        >
            <Form form={form} layout="vertical">
                <Form.Item label="Colaborador">
                    <strong>{registro?.colaboradorNombre}</strong> ({registro?.colaboradorDni})
                </Form.Item>
                <Form.Item
                    label="Días comprados"
                    name="diasComprados"
                    rules={[{ required: true, message: "Ingrese los días comprados." }]}
                >
                    <InputNumber min={0.5} step={1} style={{ width: "100%" }} />
                </Form.Item>
                <Form.Item label="Fecha Emisión (opcional)" name="fechaEmision">
                    <DatePicker format="DD/MM/YYYY" style={{ width: "100%" }} />
                </Form.Item>
            </Form>
        </Modal>
    );
};

/* ── Contenido principal (dentro del Provider) ──────────────────── */
const CompraVacacionesContent = () => {
    const [registroEditar, setRegistroEditar] = useState(null);

    return (
        <Container
            icono={<ShoppingCartOutlined />}
            titulo="Compra de Vacaciones"
            botonExtra={
                <Space>
                    <AprobarButton servicio={CompraVacacionesService} entidad="compra(s)" />
                    <ImportarModal />
                </Space>
            }
        >
            <FiltroProgramarVacaciones />
            <TablaCompraVacaciones onEditar={(record) => setRegistroEditar(record)} />
            <EditarModal registro={registroEditar} onCerrar={() => setRegistroEditar(null)} />
        </Container>
    );
};

/* ── Page ───────────────────────────────────────────────────────── */
const CompraVacacionesPage = () => {
    return (
        <JotaiProvider>
            <CompraVacacionesContent />
        </JotaiProvider>
    );
};

export default CompraVacacionesPage;
